/**
 * Premium and benefit recalculation for coverage type changes
 */

import { v4 as uuidv4 } from 'uuid';
import { getNuclearPremium, getNuclearBenefit } from '../../../../PolicyAdmin/helpers/index';
import { calculateMemberTotalPremium } from './coverageHelpers';
import { addAmendmentTracking } from '../../shared/utils/amendmentTrackingHelper';

const BENEFIT_TYPES = {
  FUNERAL: 'FUNERAL BENEFIT',
  ACCIDENTAL: 'ACCIDENTAL BENEFIT',
  GROUP_LIFE: 'Group Life Assurance'
};

const ACCIDENTAL_BENEFIT_CAP = 20000 * 100; // BWP 20,000 cap in cents

/**
 * Create benefit object with correct structure and amendment tracking
 * @param {String} type - Benefit type
 * @param {Number} coverAmount - Cover amount in cents
 * @param {Number} premium - Premium amount
 * @param {Object} existingBenefit - Existing benefit (for uuid preservation and tracking)
 * @param {Object} oldQuotation - Old quotation (for tracking previous plan)
 * @param {String} newPlan - New plan code
 */
const createBenefit = (type, coverAmount, premium, existingBenefit, oldQuotation, newPlan) => {
  const benefitUuid = existingBenefit ? existingBenefit.uuid : uuidv4();

  let newBenefit = {
    type: type,
    cover_amount_in_cents: coverAmount,
    waiting_period_in_days: 30,
    cover_usage_count: 0,
    benefit_period_cardinality: 1,
    benefit_period_granuality: 'year',
    premium_amount: premium,
    is_initialized: true,
    uuid: benefitUuid,
    additional_details: {
      premium_amount: premium,
      uuid: benefitUuid,
    }
  };

  // Add amendment tracking if we have existing benefit and quotation info
  if (existingBenefit && oldQuotation && newPlan) {
    newBenefit = addAmendmentTracking(newBenefit, existingBenefit, oldQuotation, newPlan, 'change_coverage');
  }

  return newBenefit;
};

/**
 * Identify which benefit category a benefit belongs to
 */
const identifyBenefitCategory = (benefit) => {
  const benefitName = benefit.type || benefit.name || '';

  if (benefitName.toLowerCase().includes('tombstone')) return 'add_ons';
  if (benefitName.toLowerCase().includes('waiver')) return 'premium_waivers';
  if (benefitName.toLowerCase().includes('annuit')) return 'annuities';
  if (benefitName.toLowerCase().includes('group life')) return 'additionals';

  // Default search all categories
  return null;
};

/**
 * Reprice additional benefit for new coverage type
 * Retains benefit but updates premium based on coverage type
 */
const repriceBenefitForCoverageType = (benefit, plan, newCoverageType) => {
  const benefitName = benefit.type || benefit.name;

  // Categories to search
  const categories = ['add_ons', 'premium_waivers', 'annuities', 'additionals'];

  // Try specific category first if identified
  const specificCategory = identifyBenefitCategory(benefit);
  if (specificCategory) {
    categories.unshift(specificCategory);
  }

  for (const category of categories) {
    const planBenefits = plan.membership_configs.content[category] || [];

    // Find matching benefit for new coverage type
    const matchedBenefit = planBenefits.find(pb => {
      const nameMatch = pb.name === benefitName ||
                       pb.name === benefit.type ||
                       (benefit.additional_details && pb.name === benefit.additional_details.name);

      const coverageMatch = pb.allowed_relationships &&
                           pb.allowed_relationships.includes(newCoverageType);

      return nameMatch && coverageMatch;
    });

    if (matchedBenefit) {
      // Keep benefit, update premium
      return {
        ...benefit,
        premium_amount: matchedBenefit.premium_amount_in_cents || matchedBenefit.premium_amount || 0,
        cover_amount_in_cents: matchedBenefit.benefit_amount_in_cents || benefit.cover_amount_in_cents,
        additional_details: {
          ...benefit.additional_details,
          premium_amount: matchedBenefit.premium_amount_in_cents || matchedBenefit.premium_amount || 0
        }
      };
    }
  }

  // Benefit not available for new coverage type - drop it
  console.log(`Benefit ${benefitName} not available for ${newCoverageType}, dropping`);
  return null;
};

/**
 * Recalculate benefits for new Principal when coverage changes
 * Completely recalculates all benefits from scratch based on new coverage type
 *
 * @param {Object} member - Member being promoted to Principal
 * @param {Object} plan - Plan object
 * @param {String} newCoverageType - Target coverage type
 * @param {Object} oldQuotation - Old quotation for amendment tracking
 * @returns {Array} - New benefits array
 */
export const recalculateBenefitsForNewPrincipal = (member, plan, newCoverageType, oldQuotation) => {
  const funeralPremium = getNuclearPremium(plan, newCoverageType);
  const funeralBenefit = getNuclearBenefit(plan);
  const newPlanCode = plan.code || plan.name.replace(/ /g, '_');

  console.log(`Recalculating benefits for ${member.first_name} as Principal (${newCoverageType})`);
  console.log(`Funeral Premium: ${funeralPremium}, Benefit: ${funeralBenefit}`);

  const newBenefits = [];

  // Find existing benefits for UUID preservation
  const existingFuneral = member.benefits.find(b => b.type === BENEFIT_TYPES.FUNERAL);
  const existingAccidental = member.benefits.find(b => b.type === BENEFIT_TYPES.ACCIDENTAL);

  // 1. FUNERAL BENEFIT - Principal pays full premium with amendment tracking
  newBenefits.push(createBenefit(
    BENEFIT_TYPES.FUNERAL,
    funeralBenefit,
    funeralPremium,
    existingFuneral,
    oldQuotation,
    newPlanCode
  ));

  // 2. ACCIDENTAL BENEFIT - Always premium = 0, capped at BWP 20,000
  newBenefits.push(createBenefit(
    BENEFIT_TYPES.ACCIDENTAL,
    Math.min(funeralBenefit, ACCIDENTAL_BENEFIT_CAP),
    0,
    existingAccidental,
    oldQuotation,
    newPlanCode
  ));

  // 3. Additional Benefits - Reprice for new coverage type
  const existingAdditional = member.benefits.filter(b =>
    b.type !== BENEFIT_TYPES.FUNERAL && b.type !== BENEFIT_TYPES.ACCIDENTAL
  );

  existingAdditional.forEach(benefit => {
    const repricedBenefit = repriceBenefitForCoverageType(benefit, plan, newCoverageType);
    if (repricedBenefit) {
      newBenefits.push(repricedBenefit);
    }
  });

  const totalPremium = calculateMemberTotalPremium(newBenefits);
  console.log(`Total Premium after recalculation: ${totalPremium / 100}`);

  return newBenefits;
};

/**
 * Recalculate benefits for existing Principal when coverage changes
 * (no swap, just coverage type change)
 *
 * @param {Object} member - Current Principal member
 * @param {Object} plan - Plan object
 * @param {String} newCoverageType - Target coverage type
 * @param {Object} oldQuotation - Old quotation for amendment tracking
 * @returns {Object} - Updated member object
 */
export const recalculatePrincipalBenefits = (member, plan, newCoverageType, oldQuotation) => {
  const newBenefits = recalculateBenefitsForNewPrincipal(member, plan, newCoverageType, oldQuotation);
  const newPremium = calculateMemberTotalPremium(newBenefits);

  return {
    ...member,
    benefits: newBenefits,
    premium_amount_in_cents: newPremium
  };
};

/**
 * Build quotation payload for coverage change
 *
 * For nuclear family products (Access Bank Monamodi), the billing model is a
 * FLAT RATE per coverage tier, not a per-member rate:
 *   - "Member Only"    → flat premium on the Principal entry only
 *   - "Member & Family"→ flat premium on the Principal entry only;
 *                        all other family members (Spouse, Children, Parents,
 *                        Extended) carry premium_amount_in_cents = 0
 *
 * Sending individual premiums per member causes the backend reducer
 * (quotation.mapper.ts) to sum them, producing an inflated total.
 *
 * @param {Object} policy           - Current policy
 * @param {Object} finalPrincipal   - The member who will be the Principal
 *                                    (already recalculated with flat family rate)
 * @param {String} newCoverageType  - Target coverage type
 * @param {Array}  allMembers       - All members on the policy (including non-principal).
 *                                    Used for upgrade so family members are preserved
 *                                    with premium_amount_in_cents = 0. Ignored for
 *                                    downgrade (only the Principal entry is billed).
 * @returns {Object} - Quotation payload
 */
export const buildCoverageChangeQuotation = (policy, finalPrincipal, newCoverageType, allMembers = []) => {
  // Remove secondary_plan if changing to Member Only (no parents allowed)
  const shouldIncludeSecondaryPlan = newCoverageType === 'Member & Family' &&
                                     policy.quotation.additional_details.secondary_plan;

  const additionalDetails = {
    ...policy.quotation.additional_details,
    cover_type: newCoverageType, // KEY CHANGE
    primary_plan: policy.quotation.additional_details.primary_plan
  };

  // Only include secondary_plan if staying Member & Family
  if (shouldIncludeSecondaryPlan) {
    additionalDetails.secondary_plan = policy.quotation.additional_details.secondary_plan;
  }

  // Set waiting_period_in_days to 150 for all benefits (coverage change affects all members)
  const principalBenefits = (finalPrincipal.benefits || []).map(benefit => ({
    ...benefit,
    waiting_period_in_days: 150
  }));

  // Principal entry — carries the full flat-rate premium
  const principalEntry = {
    guid: finalPrincipal.guid,
    first_name: finalPrincipal.first_name,
    middle_name: finalPrincipal.middle_name || '',
    last_name: finalPrincipal.last_name,
    msisdn: finalPrincipal.msisdn || finalPrincipal.mobile_number,
    date_of_birth: finalPrincipal.date_of_birth,
    gender: finalPrincipal.gender,
    national_id: finalPrincipal.national_id || 'N/A',
    passport: finalPrincipal.passport || 'N/A',
    postal_address: finalPrincipal.postal_address || '',
    residential_address: finalPrincipal.residential_address || '',
    relationship: 'Principal',
    type: 'Nuclear',
    // Use the pre-calculated flat-rate premium — do NOT re-sum individual
    // benefit premiums, as that would produce a per-member total.
    premium_amount_in_cents: finalPrincipal.premium_amount_in_cents,
    extended_premium: 0,
    add_on_premium: 0,
    benefits: principalBenefits,
    uuid: finalPrincipal.uuid,
    additional_details: finalPrincipal.additional_details || {}
  };

  // Non-principal entries — preserved for member records but billed at 0
  // (their coverage is included in the Principal's flat family rate).
  // Filter by relationship only — a dual uuid+relationship condition would
  // silently drop members whose uuid is undefined.
  if (newCoverageType === 'Member & Family' && allMembers.length <= 1) {
    console.warn(
      'buildCoverageChangeQuotation: upgrading to Member & Family but allMembers ' +
      'contains only the principal or is empty — family members will be missing from the payload'
    );
  }

  const nonPrincipalEntries = allMembers
    .filter(m => m.relationship !== 'Principal')
    .map(member => ({
      guid: member.guid,
      first_name: member.first_name,
      middle_name: member.middle_name || '',
      last_name: member.last_name,
      msisdn: member.msisdn || member.mobile_number || '',
      date_of_birth: member.date_of_birth,
      gender: member.gender,
      national_id: member.national_id || 'N/A',
      passport: member.passport || 'N/A',
      postal_address: member.postal_address || '',
      residential_address: member.residential_address || '',
      relationship: member.relationship,
      type: member.type || 'Nuclear',
      // Zero premium — covered under the Principal's flat family rate
      premium_amount_in_cents: 0,
      extended_premium: 0,
      add_on_premium: 0,
      benefits: (member.benefits || []).map(b => ({
        ...b,
        waiting_period_in_days: 150,
        premium_amount: 0,
        additional_details: {
          ...(b.additional_details || {}),
          premium_amount: 0
        }
      })),
      // uuid and guid are distinct fields — do not conflate them.
      // Generate a new uuid if absent rather than substituting guid.
      uuid: member.uuid || uuidv4(),
      additional_details: member.additional_details || {}
    }));

  return {
    product_guid: policy.product_guid,
    product_name: policy.product_name,
    product_code: policy.product_code,
    branch: policy.branch || 'string',
    customer_guid: policy.customer_guid,
    partner: policy.partner,
    lapse_period: policy.lapse_period || 12,
    lapse_period_granularity: policy.lapse_period_granularity || 'month',
    lapse_period_cardinality: policy.lapse_period_cardinality || 1,
    premium_granularity: policy.premium_granularity || 'month',
    premium_cardinality: policy.premium_cardinality || 1,

    policy_schedule_entries: [principalEntry, ...nonPrincipalEntries],

    additional_details: additionalDetails
  };
};
